import { actionForGamepad, type AppAction } from './actions';

export type PadAction = AppAction | { readonly kind: 'adjust-value'; readonly delta: -1 | 1 };
export interface PadState { readonly index: number; readonly connected: boolean; readonly buttons: readonly { readonly pressed: boolean; readonly value: number }[]; readonly axes: readonly number[]; }

/** Edge-triggered product controls. No session, clock or simulation authority. */
export function createPadReader(): (pads: readonly (PadState | null)[], active: boolean) => PadAction[] {
  let previous = new Set<string>();
  let suspended = false;
  return (pads, active) => {
    const held = new Map<string, PadAction>();
    for (const pad of pads) {
      if (!pad?.connected) continue;
      for (let i = 0; i < pad.buttons.length; i++) {
        if (!pad.buttons[i]?.pressed && (pad.buttons[i]?.value ?? 0) < 0.5) continue;
        const action: PadAction | null = i === 14 || i === 15 ? { kind: 'adjust-value', delta: i === 14 ? -1 : 1 } : actionForGamepad(i);
        if (action) held.set(`${pad.index}:b${i}`, action);
      }
      for (const [axis, kind] of [[0, 'adjust-value'], [1, 'move-focus']] as const) {
        const value = pad.axes[axis] ?? 0;
        if (Math.abs(value) > 0.65) held.set(`${pad.index}:a${axis}:${Math.sign(value)}`, { kind, delta: value < 0 ? -1 : 1 });
      }
    }
    const actions = active && !suspended ? [...held].filter(([key]) => !previous.has(key)).map(([, action]) => action) : [];
    previous = new Set(held.keys());
    suspended = !active;
    return actions;
  };
}

export function moveFocus(scope: HTMLElement, delta: -1 | 1): void {
  const controls = [...scope.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex="0"]')].filter(el =>
    !el.closest('[hidden], [inert], [aria-hidden="true"]') && !(el as HTMLButtonElement).disabled && getComputedStyle(el).display !== 'none' && getComputedStyle(el).visibility !== 'hidden');
  if (!controls.length) return;
  const at = controls.indexOf(scope.ownerDocument.activeElement as HTMLElement);
  const next = at < 0 ? delta === 1 ? 0 : controls.length - 1 : (at + delta + controls.length) % controls.length;
  controls[next]!.focus();
  controls[next]!.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
}

export function adjustFocusedValue(document: Document, delta: -1 | 1): boolean {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement && (el.type === 'range' || el.type === 'number')) {
    if (delta > 0) el.stepUp(); else el.stepDown();
  } else if (el instanceof HTMLSelectElement) {
    el.selectedIndex = Math.max(0, Math.min(el.options.length - 1, el.selectedIndex + delta));
  } else return false;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

/** Poll only an attached pad in a visible, focused page; held buttons never repeat on return. */
export function mountPadInput(onAction: (action: PadAction) => void, win: Window = window): () => void {
  const read = createPadReader();
  let timer = 0;
  const pads = (): readonly (Gamepad | null)[] => { try { return [...win.navigator.getGamepads()]; } catch { return []; } };
  const stop = () => { if (timer) win.clearTimeout(timer); timer = 0; read(pads(), false); };
  const poll = () => {
    timer = 0;
    if (win.document.hidden || !win.document.hasFocus()) { stop(); return; }
    const current = pads();
    for (const action of read(current, true)) onAction(action);
    if (current.some(pad => pad?.connected)) timer = win.setTimeout(poll, 50);
  };
  const resume = () => { if (!timer) poll(); };
  const visibility = () => { if (win.document.hidden) stop(); else resume(); };
  win.addEventListener('gamepadconnected', resume);
  win.addEventListener('gamepaddisconnected', resume);
  win.addEventListener('focus', resume);
  win.addEventListener('blur', stop);
  win.document.addEventListener('visibilitychange', visibility);
  resume();
  return () => { stop(); win.removeEventListener('gamepadconnected', resume); win.removeEventListener('gamepaddisconnected', resume); win.removeEventListener('focus', resume); win.removeEventListener('blur', stop); win.document.removeEventListener('visibilitychange', visibility); };
}
